import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { isAdminUser } from './AdminConsole';
import About from './About';
import Media from './Media';
import Video from './Video';
import Email from './Email';
import VibeCheck from './VibeCheck';
import AbelAlignment from './AbelAlignment';
import SplashScreen from './SplashScreen';
import AgeVerification from './AgeVerification';
import memory from '../utils/memory';

import Footer from './Footer';
import BackGroundImg from '../images/purple-web.png';
import BackGroundImgWebP from '../images/purple-web.webp';

const SESSION_TTL = 7 * 60 * 1000; // 7 minutes

function loadSession() {
  try {
    const raw = localStorage.getItem('sessionData');
    if (!raw) return false;
    const { verified, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp < SESSION_TTL) return verified;
    localStorage.removeItem('sessionData');
    return false;
  } catch {
    localStorage.removeItem('sessionData');
    return false;
  }
}

function saveSession() {
  try {
    localStorage.setItem(
      'sessionData',
      JSON.stringify({ verified: true, timestamp: Date.now() })
    );
  } catch {
    // storage not available — continue without persisting
  }
}

const Main = ({ onVerificationChange }) => {
  const [showSplash, setShowSplash] = useState(true);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [verified, setVerified] = useState(false);

  const onVerificationChangeRef = useRef(onVerificationChange);
  onVerificationChangeRef.current = onVerificationChange;

  const verifiedRef = useRef(false);
  verifiedRef.current = verified;

  // Notify parent whenever gate state changes
  useEffect(() => {
    const inGate = showSplash || showAgeVerification;
    onVerificationChangeRef.current?.(verified, inGate);
  }, [showSplash, showAgeVerification, verified]);

  // Bypass all gates instantly when admin is logged in
  useEffect(() => {
    return onAuthStateChanged(auth, user => {
      if (isAdminUser(user?.email)) {
        setShowSplash(false);
        setShowAgeVerification(false);
        setVerified(true);
      }
    });
  }, []);

  // Check for a valid session on mount
  useEffect(() => {
    setVerified(loadSession());
    try {
      memory.updateVisitInfo();
    } catch {
      /* ignore */
    }
  }, []);

  // Re-show age verification every 5 minutes while user is on the main content
  useEffect(() => {
    if (!showSplash && !showAgeVerification && verified) {
      const t = setTimeout(
        () => {
          setShowAgeVerification(true);
          setVerified(false);
        },
        5 * 60 * 1000
      );
      return () => clearTimeout(t);
    }
  }, [showSplash, showAgeVerification, verified]);

  const handleSplashEnd = useCallback(() => {
    setShowSplash(false);
    if (!verifiedRef.current) {
      setShowAgeVerification(true);
    }
  }, []);

  const handleVerification = useCallback(() => {
    saveSession();
    setVerified(true);
    setShowAgeVerification(false);
    try {
      memory.updatePreferences({ showAgeVerification: false });
    } catch {
      /* ignore */
    }
  }, []);

  if (showSplash) {
    return <SplashScreen onEnd={handleSplashEnd} />;
  }

  if (showAgeVerification) {
    return <AgeVerification onVerified={handleVerification} />;
  }

  return (
    <Wrapper>
      <div id="abelicious">
        <About />
      </div>
      <div id="music">
        <Media />
      </div>
      <div id="video">
        <Video />
      </div>
      <div id="alignment">
        <AbelAlignment />
      </div>
      <div id="vibecheck">
        <VibeCheck />
      </div>
      <div id="home">
        <Email />
      </div>
      <Footer />
    </Wrapper>
  );
};

export default Main;

const Wrapper = styled.main`
  width: 100%;
  max-width: 100vw;
  min-height: 100vh;
  background-image: image-set(
    url(${BackGroundImgWebP}) type('image/webp'),
    url(${BackGroundImg}) type('image/png')
  );
  /* Tile the pattern — never zoom/crop it */
  background-repeat: repeat;
  background-size: 420px 420px;
  background-position: top left;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 1024px) {
    background-size: 320px 320px;
    background-attachment: scroll;
  }

  @media (max-width: 768px) {
    padding: 15px;
    background-size: 260px 260px;
    background-attachment: scroll;
  }

  @media (max-width: 430px) {
    padding: 10px;
    background-size: 195px 195px;
    background-attachment: scroll;
  }

  @media (max-width: 390px) {
    background-size: 180px 180px;
  }
`;
